import { FC, useRef, useEffect, SyntheticEvent, useCallback } from 'react';
import cls from 'classnames';
import { GalleryLabelType } from 'shared/models';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { Link } from '@remix-run/react';
import { useViewObserver } from '@lemon8/web-app-shared/util';
import { ClickPosition } from '~/utils/click-position';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { $AdditionalParams } from '~/models/additional-params';

import './style.scss';

interface ImgLabelProps {
  label: GalleryLabelType;
  show: boolean;
}

export const GalleryLabel: FC<ImgLabelProps> = (props) => {
  const { label, show } = props;

  const { direction, id, name, xPct, yPct } = label;
  const { region } = useAtomValue($Locale);

  const isLeft = direction === 'left';
  const prefix = useScopedAtomValue($CurrentPositionPrefix);
  const additionalParams = useScopedAtomValue($AdditionalParams);

  const observedNameRef = useViewObserver<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: prefix + ClickPosition.main_gallery_label,
        ...additionalParams,
        is_cta: 0,
      });
    }, []),
  });

  useEffect(() => {
    const galleryName = observedNameRef.current;
    if (!galleryName) {
      return;
    }

    const callback = (e: TouchEvent): void => {
      e.stopPropagation();
    };

    galleryName.addEventListener('touchstart', callback);
    return () => galleryName.removeEventListener('touchstart', callback);
  }, []);

  const handleClick = (e: SyntheticEvent): void => {
    e.stopPropagation();
    window.Tea('wap_click', {
      current_position: prefix + ClickPosition.main_gallery_label,
      page_click_id: id,
      is_cta: 0,
    });
  };

  /**
   * 兜底坐标
   *
   * @param pos - 服务器传来的坐标
   */
  const calcPos = (pos: number) => {
    if (pos < 0.03) {
      return 0.03;
    } else if (pos > 0.97) {
      return 0.97;
    }
    return pos;
  };

  return (
    <div
      className={cls('gallery-label-wrapper', {
        isShow: show,
      })}
      style={{
        left: `${calcPos(xPct) * 100}%`,
        top: `${calcPos(yPct) * 100}%`,
      }}
    >
      <div className={cls('label-dot-wrapper', { left: isLeft })}>
        <div className="label-dot-border" />
        <div className="label-dot" />
      </div>
      <Link
        to={`/topic/${id}?region=${region}`}
        className={cls('label-name', { left: isLeft, right: !isLeft })}
        ref={observedNameRef}
        onClick={handleClick}
      >
        #{name}
      </Link>
    </div>
  );
};
